import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, ChevronRight } from "lucide-react";
import { CATALOG, findFamily, findItem, familyTarget, familyTabs } from "@/lib/experienceCatalog";

// Card-based navigation for classic experiences inside the new dashboard:
//   /app/{area}            → family cards (one card per family, e.g. HAVEN)
//   /app/{area}/{family}   → the family's experiences as cards
//   /app/{area}/{item}     → the experience embedded in place (original URL, same auth)
export function resolveCatalog(areaKey, sub) {
  if (!sub) return { view: "families" };
  const hit = findItem(areaKey, sub);
  if (hit) return { view: "item", ...hit };
  const family = findFamily(areaKey, sub);
  if (family) return { view: "family", family };
  return { view: "families" };
}

export function CatalogCards({ areaKey, heading, testid }) {
  const navigate = useNavigate();
  const families = CATALOG[areaKey] || [];
  return (
    <section className="px-8 pb-8" data-testid={testid || `catalog-${areaKey}`}>
      {heading && <h2 className="text-xs uppercase tracking-[0.18em] text-[var(--qn-foreground-subtle)] mb-3">{heading}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {families.map((f) => {
          const Icon = f.icon;
          return (
            <button key={f.id} data-testid={`catalog-card-${f.id}`} onClick={() => navigate(familyTarget(areaKey, f))}
              className="group text-left rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 hover:border-[var(--qn-primary)]/50 hover:-translate-y-0.5 transition-[transform,border-color] focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-[var(--qn-primary-muted)] flex items-center justify-center"><Icon className="w-4.5 h-4.5 text-[var(--qn-primary)]" /></span>
                <span className="font-semibold">{f.label}</span>
                <ChevronRight className="ml-auto w-4 h-4 text-[var(--qn-foreground-subtle)] group-hover:text-[var(--qn-primary)] transition-colors" />
              </div>
              <p className="mt-3 text-sm text-[var(--qn-foreground-muted)]">{f.desc}</p>
              <p className="mt-2 text-[11px] text-[var(--qn-foreground-subtle)]">
                {f.items.length === 1 ? f.items[0].label
                  : f.defaultItem ? `Opens ${f.items.find((i) => i.id === f.defaultItem)?.label} · tabs: ${f.items.map((i) => i.label).join(" · ")}`
                  : `${f.items.length} experiences · ${f.items.map((i) => i.label).join(" · ")}`}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function FamilyItems({ areaKey, family }) {
  const navigate = useNavigate();
  return (
    <section className="px-8 pb-8" data-testid={`catalog-family-${family.id}`}>
      <button onClick={() => navigate(`/app/${areaKey}`)} data-testid="catalog-back"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)] mb-4">
        <ArrowLeft className="w-4 h-4" /> All
      </button>
      <h2 className="text-lg font-semibold mb-1">{family.label}</h2>
      <p className="text-sm text-[var(--qn-foreground-muted)] mb-4">{family.desc}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {family.items.map((it) => (
          <button key={it.id} data-testid={`catalog-item-${it.id}`}
            onClick={() => (it.internal ? navigate(it.path) : navigate(`/app/${areaKey}/${it.id}`))}
            className="text-left rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 hover:border-[var(--qn-primary)]/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">
            <div className="font-medium">{it.label}</div>
            {it.desc && <p className="mt-1.5 text-sm text-[var(--qn-foreground-muted)]">{it.desc}</p>}
          </button>
        ))}
      </div>
    </section>
  );
}

export function EmbeddedItem({ areaKey, family, item }) {
  const navigate = useNavigate();
  const tabs = familyTabs(family);
  const backTo = (family.items.length === 1 || tabs.length) ? `/app/${areaKey}` : `/app/${areaKey}/${family.id}`;
  const fullPath = item.path.replace(/([?&])embed=1&?/, "$1").replace(/[?&]$/, "");
  return (
    <section data-testid={`catalog-embed-${item.id}`}>
      <div className="px-8 pb-3 flex items-center gap-3 text-sm">
        <button onClick={() => navigate(backTo)} data-testid="catalog-back"
          className="inline-flex items-center gap-1.5 text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]">
          <ArrowLeft className="w-4 h-4" /> {tabs.length ? "All" : family.label}
        </button>
        <span className="text-[var(--qn-foreground-subtle)]">/</span>
        {tabs.length ? (
          <div className="flex items-center gap-1" role="tablist" data-testid={`catalog-tabs-${family.id}`}>
            {tabs.map((t) => (
              <button key={t.id} role="tab" aria-selected={t.id === item.id} data-testid={`catalog-tab-${t.id}`}
                onClick={() => (t.internal ? navigate(t.path) : navigate(`/app/${areaKey}/${t.id}`))}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${t.id === item.id
                  ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]"
                  : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]"}`}>
                {t.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="font-medium" data-testid="catalog-embed-title">{item.label}</span>
        )}
        <a href={fullPath} target="_blank" rel="noreferrer" data-testid="catalog-open-full"
          className="ml-auto inline-flex items-center gap-1 text-xs text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-primary)]">
          Open full screen <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <iframe title={item.label} src={item.path} data-testid="catalog-embed-frame"
        className="w-full border-t border-[var(--qn-border)] bg-[var(--qn-background)]"
        style={{ height: "calc(100vh - 150px)" }} />
    </section>
  );
}

// Complete area = header + catalog navigation (used by Settings and Platform Admin).
export function CatalogArea({ areaKey, sub, header }) {
  const r = resolveCatalog(areaKey, sub);
  return (
    <div className="min-h-screen" data-testid={`${areaKey}-area`}>
      {header}
      {r.view === "families" && <CatalogCards areaKey={areaKey} />}
      {r.view === "family" && <FamilyItems areaKey={areaKey} family={r.family} />}
      {r.view === "item" && <EmbeddedItem areaKey={areaKey} family={r.family} item={r.item} />}
    </div>
  );
}
